import React, { useId, useMemo } from "react";
import {
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  formatDailyRecapDate,
  getDailyRecapSections,
} from "../utils/dailyRecapDisplay";
import type {
  DailyRecapRow,
  DailyRecapSection,
} from "../utils/dailyRecapDisplay";
import "./DailyRecapDetail.css";

type DailyRecapData = Record<string, unknown>;

interface DailyRecapBaseProps {
  data?: DailyRecapData | null;
  createdAt?: string | null;
  companyNameFallback?: string | null;
  warehouseNameFallback?: string | null;
}

interface DailyRecapDetailProps extends DailyRecapBaseProps {
  message?: string | null;
  showContext?: boolean;
}

interface DailyRecapHighlight extends DailyRecapRow {
  tone: "blue" | "green" | "violet" | "amber";
}

const hasOwn = (data: DailyRecapData, key: string) =>
  Object.prototype.hasOwnProperty.call(data, key);

const asText = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const asNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeEntityName = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("fr-FR");

function buildEntityLabel({
  data = {},
  companyNameFallback,
  warehouseNameFallback,
}: DailyRecapBaseProps) {
  const companyName =
    asText(data?.company_name) || asText(companyNameFallback);
  const warehouseName =
    asText(data?.warehouse_name) || asText(warehouseNameFallback);
  const names = companyName ? [companyName] : [];

  if (
    warehouseName &&
    (!companyName ||
      normalizeEntityName(warehouseName) !== normalizeEntityName(companyName))
  ) {
    names.push(warehouseName);
  }

  return names.join(" · ");
}

function formatReceivedAt(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "full",
    timeStyle: "short",
  }).format(date);
}

function buildHighlights(
  data: DailyRecapData,
  sections: DailyRecapSection[]
): DailyRecapHighlight[] {
  const rowByKey = new Map(
    sections.flatMap((section) => section.rows).map((row) => [row.key, row])
  );
  const performanceKey = hasOwn(data, "actual_margin")
    ? "actual_margin"
    : "net_cash_flow";
  const activityKey = hasOwn(data, "prepared_items_count")
    ? "prepared_items_count"
    : hasOwn(data, "sales_count")
      ? "sales_count"
      : hasOwn(data, "unpaid_sales_amount")
        ? "unpaid_sales_amount"
        : "total_purchases";
  const preferred = [
    { key: "total_sales", tone: "blue" as const },
    { key: "total_payments_in", tone: "green" as const },
    { key: performanceKey, tone: "violet" as const },
    { key: activityKey, tone: "amber" as const },
  ];

  return preferred.flatMap(({ key, tone }) => {
    const row = rowByKey.get(key);
    return row ? [{ ...row, tone }] : [];
  });
}

function buildAttentionRows(
  data: DailyRecapData,
  sections: DailyRecapSection[]
) {
  const rows = sections.flatMap((section) => section.rows);
  const rowByKey = new Map(rows.map((row) => [row.key, row]));
  const selectedRows: DailyRecapRow[] = [];
  const reservedKeys = new Set<string>();

  if (asNumber(data.open_anomaly_count) > 0) {
    const anomalyRows = rows.filter((row) => row.key.startsWith("open_anomaly_"));
    reservedKeys.add("open_anomaly_count");
    anomalyRows.forEach((row) => reservedKeys.add(row.key));
    if (anomalyRows.length > 0) {
      selectedRows.push(...anomalyRows);
    } else {
      const countRow = rowByKey.get("open_anomaly_count");
      if (countRow) selectedRows.push(countRow);
    }
  }

  const unavailableAnomalies = rowByKey.get("open_anomalies_note");
  if (unavailableAnomalies) {
    reservedKeys.add(unavailableAnomalies.key);
    selectedRows.push(unavailableAnomalies);
  }

  if (asNumber(data.unpaid_sales_amount) > 0) {
    const unpaidRow = rowByKey.get("unpaid_sales_amount");
    reservedKeys.add("unpaid_sales_amount");
    reservedKeys.add("unpaid_sales_count");
    if (unpaidRow) selectedRows.push(unpaidRow);
  }

  if (asNumber(data.unsold_value_rate) > 0) {
    const unsoldRow = rowByKey.get("unsold_value_rate");
    reservedKeys.add("unsold_value_rate");
    if (unsoldRow) selectedRows.push(unsoldRow);
  }

  rows
    .filter((row) => row.key.startsWith("dish_to_watch_"))
    .forEach((row) => {
      reservedKeys.add(row.key);
      selectedRows.push(row);
    });

  return { rows: selectedRows, reservedKeys };
}

export const DailyRecapModalTitle: React.FC<DailyRecapBaseProps> = (props) => {
  const data = props.data || {};
  const dateValue = data.recap_date || props.createdAt;
  const entityLabel = buildEntityLabel(props);

  return (
    <div className="daily-recap-modal-title">
      <span className="daily-recap-modal-title__icon" aria-hidden="true">
        <CheckCircleOutlined />
      </span>
      <span className="daily-recap-modal-title__copy">
        <span className="daily-recap-modal-title__heading">
          {dateValue
            ? `Clôture du ${formatDailyRecapDate(dateValue)}`
            : "Bilan journalier"}
        </span>
        <span className="daily-recap-modal-title__subtitle">
          {[entityLabel, "Bilan journalier"].filter(Boolean).join(" · ")}
        </span>
      </span>
    </div>
  );
};

const DailyRecapDetail: React.FC<DailyRecapDetailProps> = ({
  data = {},
  message,
  createdAt,
  showContext = true,
}) => {
  const attentionTitleId = useId();
  const safeData = useMemo(() => data || {}, [data]);
  const sections = useMemo(
    () => getDailyRecapSections(safeData),
    [safeData]
  );
  const highlights = useMemo(
    () => buildHighlights(safeData, sections),
    [safeData, sections]
  );
  const attention = useMemo(
    () => buildAttentionRows(safeData, sections),
    [safeData, sections]
  );
  const hiddenRowKeys = useMemo(
    () =>
      new Set([
        ...highlights.map((row) => row.key),
        ...Array.from(attention.reservedKeys),
      ]),
    [attention.reservedKeys, highlights]
  );
  const visibleSections = useMemo(
    () =>
      sections.flatMap((section) => {
        const rows = section.rows.filter((row) => !hiddenRowKeys.has(row.key));
        return rows.length > 0 ? [{ ...section, rows }] : [];
      }),
    [hiddenRowKeys, sections]
  );
  const receivedAt = formatReceivedAt(createdAt);
  const hasStructuredContent = sections.length > 0;

  return (
    <div className="daily-recap-detail">
      {showContext ? (
        <div className="daily-recap-detail__context">
          <p>
            Vue synthétique de l’activité, de la trésorerie et des points
            d’attention de la journée.
          </p>
          <span className="daily-recap-detail__status">
            <CheckCircleOutlined aria-hidden="true" /> Bilan disponible
          </span>
        </div>
      ) : null}

      {attention.rows.length > 0 && (
        <section
          className="daily-recap-detail__attention"
          aria-labelledby={attentionTitleId}
        >
          <WarningOutlined className="daily-recap-detail__attention-icon" aria-hidden="true" />
          <div>
            <h3 id={attentionTitleId}>
              {attention.rows.length === 1
                ? "Point à surveiller"
                : "Points à surveiller"}
            </h3>
            <dl>
              {attention.rows.map((row) => (
                <div key={row.key}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {hasStructuredContent ? (
        <>
          {highlights.length > 0 && (
            <section
              className="daily-recap-detail__highlights"
              aria-label="Indicateurs clés"
            >
              {highlights.map((row) => (
                <article
                  key={row.key}
                  className={`daily-recap-detail__highlight daily-recap-detail__highlight--${row.tone}`}
                >
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </article>
              ))}
            </section>
          )}

          {visibleSections.length > 0 && (
            <div className="daily-recap-detail__sections">
              {visibleSections.map((section) => (
                <section
                  key={section.key}
                  className={`daily-recap-detail__section daily-recap-detail__section--${section.tone}`}
                >
                  <header>
                    <h3>{section.title}</h3>
                    <p>{section.description}</p>
                  </header>
                  <dl>
                    {section.rows.map((row) => (
                      <div
                        key={row.key}
                        className={
                          row.span === 2
                            ? "daily-recap-detail__row--wide"
                            : undefined
                        }
                      >
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          )}
        </>
      ) : (
        <section className="daily-recap-detail__fallback">
          <span>Résumé</span>
          <p>{message || "Aucune opération enregistrée pour cette journée."}</p>
        </section>
      )}

      {receivedAt && (
        <p className="daily-recap-detail__received">Notification reçue le {receivedAt}</p>
      )}
    </div>
  );
};

export default DailyRecapDetail;
